import { Resident } from 'src/store/sageAdminApi';

interface ResidentDisplayOptions {
    usePreferred?: boolean;
    justInitials?: boolean;
    firstOnly?: boolean;
    initializeFirst?: boolean;
    initializeMiddle?: boolean;
    initializeLast?: boolean;
    includeMiddleName?: boolean;
}

/**
 * Formats a resident's name.
 * @param resident The resident object.
 * @param options Formatting options.
 * - usePreferred: Whether to use the preferred name if available.
 * - justInitials: Whether to return just the initials (overrides initializeFirst/Last/Middle).
 * - firstOnly: Whether to return only the first name, for components that pass in first name separately.
 * - includeMiddleName: Whether to include the middle name.
 * - initializeFirst: Whether to initialize the first name (e.g., "J." instead of "John").
 * - initializeLast: Whether to initialize the last name (e.g., "D." instead of "Doe").
 * - initializeMiddle: Whether to initialize the middle name (e.g., "M." instead of "Michael").
 * @returns The formatted name as a string (e.g., "John Doe", "J. Doe", "John M. Doe", "J. M. D.").
 */
export const getResidentDisplayName = (
    resident?: Resident,
    options: ResidentDisplayOptions = {}
) => {
    const {
        usePreferred = true,
        firstOnly = false,
        justInitials = false,
        includeMiddleName = false,
        initializeFirst = false,
        initializeLast = false,
        initializeMiddle = false,
    } = options;

    if (!resident) {
        return '';
    }

    const { preferredName, firstName, middleName, lastName } = resident;

    const effectiveFirstName = usePreferred && preferredName ? preferredName : firstName;

    // If justInitials is true, initialize all names regardless of individual settings
    const shouldInitializeFirst = justInitials || initializeFirst;
    const shouldInitializeMiddle = justInitials || initializeMiddle;
    const shouldInitializeLast = justInitials || initializeLast;

    const firstPart =
        shouldInitializeFirst && effectiveFirstName
            ? effectiveFirstName.charAt(0).toLocaleUpperCase() + '.'
            : effectiveFirstName ?? '';

    if (firstOnly) {
        return firstPart.trim();
    }

    const middlePart =
        includeMiddleName && middleName
            ? shouldInitializeMiddle
                ? middleName.charAt(0).toLocaleUpperCase() + '.'
                : middleName
            : '';

    const lastPart =
        shouldInitializeLast && lastName
            ? lastName.charAt(0).toLocaleUpperCase() + '.'
            : lastName ?? '';

    return [firstPart, middlePart, lastPart]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' ');
};

export const buildSearchableResidentName = (resident: Resident): string => {
    const { firstName = '', lastName = '', preferredName = '', middleName = '', unit } = resident;

    const parts = [firstName, preferredName, middleName, lastName, unit?.displayName ?? ''];

    const combos = [
        [firstName, middleName, lastName],
        [preferredName, middleName, lastName],
        [firstName, lastName],
        [preferredName, lastName],
    ];

    return [...parts, ...combos.map((c) => c.filter(Boolean).join(' '))]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
};
